import type {
  GrowingMedium,
  PlantDetail,
  PlantDiagnosisRecord,
  PlantPhoto,
  PlantSummary,
  VolumeBucket,
} from "../shared/types";

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  if (init?.body && !(init.body instanceof FormData) && typeof init.body === "string") {
    headers.set("Content-Type", "application/json");
  }
  const res = await fetch(path, {
    ...init,
    credentials: "include",
    headers,
  });
  if (!res.ok) {
    let msg = res.statusText;
    try {
      const j = (await res.json()) as { error?: string };
      if (j.error) msg = j.error;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  return (await res.json()) as T;
}

export function login(passcode: string) {
  return api<{ ok: boolean }>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify({ passcode }),
  });
}

export function fetchPlants() {
  return api<{ plants: PlantSummary[] }>("/api/plants");
}

export function fetchPlant(id: string) {
  return api<{ plant: PlantDetail }>(`/api/plants/${id}`);
}

export function updatePlant(
  plantId: string,
  body: { location?: string; growing_medium?: GrowingMedium | null },
) {
  return api<{ ok: boolean; location?: string | null; growing_medium?: GrowingMedium | null }>(
    `/api/plants/${plantId}`,
    {
      method: "PATCH",
      body: JSON.stringify(body),
    },
  );
}

export function createPlant(form: FormData) {
  return api<{ id: string }>("/api/plants", {
    method: "POST",
    body: form,
  });
}

export function logWatering(
  plantId: string,
  body: {
    volume_bucket: VolumeBucket;
    method_mode: "same_as_last" | "custom";
    method_text?: string;
    occurred_at?: number;
    copy_volume_from_last?: boolean;
  },
) {
  return api<{ ok: boolean; id: string }>(`/api/plants/${plantId}/water`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function updateWatering(
  plantId: string,
  eventId: string,
  body: {
    volume_bucket?: VolumeBucket;
    method_text?: string;
    occurred_at?: number;
  },
) {
  return api<{ ok: boolean; id: string }>(`/api/plants/${plantId}/water/${eventId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export function deleteWatering(plantId: string, eventId: string) {
  return api<{ ok: boolean }>(`/api/plants/${plantId}/water/${eventId}`, {
    method: "DELETE",
  });
}

export function deletePlant(id: string) {
  return api<{ ok: boolean }>(`/api/plants/${id}`, {
    method: "DELETE",
  });
}

export function reanalyzePlant(id: string) {
  return api<{ ok: boolean }>(`/api/plants/${id}/reanalyze`, {
    method: "POST",
  });
}

export function addPlantPhoto(plantId: string, form: FormData) {
  return api<{ photo: PlantPhoto }>(`/api/plants/${plantId}/photos`, {
    method: "POST",
    body: form,
  });
}

export function setPlantCoverPhoto(plantId: string, photoId: string) {
  return api<{ ok: boolean }>(`/api/plants/${plantId}/photos/${photoId}`, {
    method: "PATCH",
    body: JSON.stringify({ set_cover: true }),
  });
}

export function deletePlantPhoto(plantId: string, photoId: string) {
  return api<{ ok: boolean }>(`/api/plants/${plantId}/photos/${photoId}`, {
    method: "DELETE",
  });
}

export function diagnosePlant(plantId: string, photoIds?: string[]) {
  return api<{ diagnosis: PlantDiagnosisRecord }>(`/api/plants/${plantId}/diagnose`, {
    method: "POST",
    body: JSON.stringify(photoIds?.length ? { photo_ids: photoIds } : {}),
  });
}
